import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, downloadFile } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { DateInput } from '../shared/DateInput';
import { formatDate, formatDateTime, formatMoney } from '../shared/formatting';
import { Btn, Pill } from '../shared/ui';
import { MaskedName } from '../shared/nameMask';

type Locale = 'hr' | 'en';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const inp: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%', boxSizing: 'border-box' };
const th: React.CSSProperties = { textAlign: 'left', padding: '7px 9px', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--ad-ink)', borderBottom: '2px solid #efe6d2', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 9px', fontSize: '0.83rem', borderBottom: '1px solid #f3ecdb', verticalAlign: 'top' };
const lbl: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 600, color: '#6b5b34', marginBottom: 3, display: 'block' };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return <div style={{ marginBottom: 10 }}><span style={lbl}>{label}</span>{children}</div>;
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,5,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: wide ? 820 : 560, maxWidth: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)', fontSize: '1.05rem' }}>{title}</h3>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '1.3rem', cursor: 'pointer', color: '#8a7a52' }}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}

const LOST_STATUS: Record<string, { hr: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral' }> = {
    izgubljeno: { hr: 'Izgubljeno', tone: 'bad' },
    odgovoreno: { hr: 'Odgovoreno', tone: 'warn' },
    naplaceno: { hr: 'Naplaćeno', tone: 'info' },
    zakljuceno: { hr: 'Izgubljeno i naplaćeno', tone: 'ok' },
};

type LostRow = {
    id: number; company_code: string | null; carrier: string | null; restock_no: string | null; restock_date: string | null;
    warehouse_from: string | null; warehouse_to: string | null; order_no: string | null; items_text: string | null;
    goods_value_eur: string | null; carrier_parcel_no: string | null; reported_at: string | null; status: string;
    response_at: string | null; response_type: string | null; payout_at: string | null; payout_amount_eur: string | null;
    closed_at: string | null; attachment_name: string | null; note: string | null; created_by_name: string | null; created_at: string | null;
};

const emptyLost: Partial<LostRow> = { company_code: 'ADMS', carrier: 'HP' };

export function LostParcelsTab({ token }: { token: string; locale: Locale }) {
    const [rows, setRows] = useState<LostRow[]>([]);
    const [summary, setSummary] = useState<Record<string, number>>({});
    const [statusF, setStatusF] = useState('');
    const [q, setQ] = useState('');
    const [edit, setEdit] = useState<Partial<LostRow> | null>(null);
    const [events, setEvents] = useState<Array<{ event: string; by_name: string | null; detail: string | null; at: string | null }>>([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        const params = new URLSearchParams();
        if (statusF) params.set('status', statusF);
        if (q) params.set('q', q);
        const r = await api<{ rows: LostRow[]; summary: Record<string, number> }>(`/parcels/lost?${params}`, token);
        setRows(r.rows); setSummary(r.summary);
    }, [token, statusF, q]);

    useEffect(() => { load().catch((e) => setErr(String(e))); }, [load]);

    async function openEdit(row: LostRow) {
        setEdit({ ...row });
        try {
            const r = await api<{ events: typeof events }>(`/parcels/lost/${row.id}`, token);
            setEvents(r.events);
        } catch { setEvents([]); }
    }

    async function save() {
        if (!edit) return;
        setBusy(true); setErr('');
        try {
            const body: Record<string, unknown> = {
                company_code: edit.company_code, carrier: edit.carrier, restock_no: edit.restock_no, restock_date: edit.restock_date,
                warehouse_from: edit.warehouse_from, warehouse_to: edit.warehouse_to, order_no: edit.order_no, items_text: edit.items_text,
                goods_value_eur: edit.goods_value_eur === '' ? null : edit.goods_value_eur, carrier_parcel_no: edit.carrier_parcel_no,
                reported_at: edit.reported_at, response_at: edit.response_at, response_type: edit.response_type || null,
                payout_at: edit.payout_at, payout_amount_eur: edit.payout_amount_eur === '' ? null : edit.payout_amount_eur,
                closed_at: edit.closed_at, note: edit.note,
            };
            if (edit.id) await mutate(`/parcels/lost/${edit.id}`, token, 'PUT', body);
            else await mutate('/parcels/lost', token, 'POST', body);
            setEdit(null); await load();
        } catch (e) { setErr(String(e)); } finally { setBusy(false); }
    }

    async function del(id: number) {
        if (!window.confirm('Obrisati zapis?')) return;
        await mutate(`/parcels/lost/${id}`, token, 'DELETE'); await load();
    }

    async function uploadPdf(file: File) {
        if (!edit?.id) return;
        const fd = new FormData(); fd.append('file', file);
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/parcels/lost/${edit.id}/attach`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
            if (!res.ok) throw new Error('Upload nije uspio');
            const j = await res.json();
            setEdit({ ...edit, attachment_name: j.attachment_name });
        } catch (e) { setErr(String(e)); } finally { setBusy(false); }
    }

    const set = (k: keyof LostRow, v: unknown) => setEdit((p) => ({ ...(p ?? {}), [k]: v }));

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <select style={{ ...inp, width: 190 }} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
                    <option value="">Svi statusi</option>
                    {Object.entries(LOST_STATUS).map(([k, v]) => <option key={k} value={k}>{v.hr}</option>)}
                </select>
                <input style={{ ...inp, width: 220 }} placeholder="Pretraga (preskladištenje, narudžba, paket…)" value={q} onChange={(e) => setQ(e.target.value)} />
                <div style={{ flex: 1 }} />
                <Btn variant="primary" onClick={() => { setEdit({ ...emptyLost }); setEvents([]); }}>+ Nova prijava</Btn>
                <Btn onClick={() => downloadFile('/parcels/lost-export', token, 'izgubljene_posiljke.xlsx')}>⬇ XLS</Btn>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {[['total', 'Ukupno', '#6b5b34'], ['izgubljeno', 'Izgubljeno', '#c0392b'], ['odgovoreno', 'Odgovoreno', '#b7791f'], ['naplaceno', 'Naplaćeno', '#2b6cb0'], ['zakljuceno', 'Zaključeno', '#2f855a']].map(([k, l, c]) => (
                    <div key={k} style={{ ...card, padding: '10px 14px', minWidth: 110 }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c as string }}>{summary[k as string] ?? 0}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{l}</div>
                    </div>
                ))}
                <div style={{ ...card, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#6b5b34' }}>{formatMoney(summary.open_value ?? 0)}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>Vrijednost otvorenih</div>
                </div>
            </div>

            {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}

            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                    <thead><tr>
                        {['Firma', 'Dostavljač', 'Preskladištenje', 'Skladišta', 'Narudžba', 'Vrijednost', 'Status', 'Prijava', 'Unio', ''].map((h) => <th key={h} style={th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td style={td}>{r.company_code}</td>
                                <td style={td}>{r.carrier}</td>
                                <td style={td}>{r.restock_no}{r.restock_date ? <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{r.restock_date}</div> : null}</td>
                                <td style={td}>{r.warehouse_from}{r.warehouse_to ? ' → ' + r.warehouse_to : ''}</td>
                                <td style={td}>{r.order_no}</td>
                                <td style={td}>{r.goods_value_eur ? formatMoney(Number(r.goods_value_eur)) : '—'}</td>
                                <td style={td}><Pill tone={LOST_STATUS[r.status]?.tone ?? 'neutral'}>{LOST_STATUS[r.status]?.hr ?? r.status}</Pill></td>
                                <td style={td}>{r.reported_at ?? '—'}</td>
                                <td style={td}><span style={{ fontSize: '0.74rem', color: '#8a7a52' }}>{r.created_by_name}</span></td>
                                <td style={td}><Btn size="sm" onClick={() => openEdit(r)}>Uredi</Btn></td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td style={td} colSpan={10}>Nema zapisa.</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {edit ? (
                <Modal wide title={edit.id ? 'Izgubljena pošiljka' : 'Nova prijava izgubljene pošiljke'} onClose={() => setEdit(null)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Field label="Firma"><select style={inp} value={edit.company_code ?? ''} onChange={(e) => set('company_code', e.target.value)}><option value="ADMS">ADMS</option><option value="ADO">ADO</option></select></Field>
                        <Field label="Dostavljač"><select style={inp} value={edit.carrier ?? ''} onChange={(e) => set('carrier', e.target.value)}><option value="HP">HP</option><option value="GLS">GLS</option></select></Field>
                        <Field label="Broj preskladištenja"><input style={inp} value={edit.restock_no ?? ''} onChange={(e) => set('restock_no', e.target.value)} /></Field>
                        <Field label="Datum preskladištenja"><DateInput value={edit.restock_date ?? ''} onChange={(v) => set('restock_date', v)} /></Field>
                        <Field label="Skladište šalje"><input style={inp} value={edit.warehouse_from ?? ''} onChange={(e) => set('warehouse_from', e.target.value)} /></Field>
                        <Field label="Skladište prima"><input style={inp} value={edit.warehouse_to ?? ''} onChange={(e) => set('warehouse_to', e.target.value)} /></Field>
                        <Field label="Broj narudžbe"><input style={inp} value={edit.order_no ?? ''} onChange={(e) => set('order_no', e.target.value)} /></Field>
                        <Field label="Broj paketa dostavljača"><input style={inp} value={edit.carrier_parcel_no ?? ''} onChange={(e) => set('carrier_parcel_no', e.target.value)} /></Field>
                        <Field label="Vrijednost robe (EUR)"><input style={inp} type="number" value={edit.goods_value_eur ?? ''} onChange={(e) => set('goods_value_eur', e.target.value)} /></Field>
                        <Field label="Datum prijave dostavljaču"><DateInput value={edit.reported_at ?? ''} onChange={(v) => set('reported_at', v)} /></Field>
                    </div>
                    <Field label="Popis artikala, količine, cijene"><textarea style={{ ...inp, minHeight: 54 }} value={edit.items_text ?? ''} onChange={(e) => set('items_text', e.target.value)} /></Field>

                    <div style={{ borderTop: '1px solid #efe6d2', margin: '10px 0', paddingTop: 10 }}>
                        <div style={{ fontWeight: 700, color: '#6b5b34', fontSize: '0.85rem', marginBottom: 8 }}>Tijek (status se izvodi automatski)</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <Field label="Datum odgovora dostavljača"><DateInput value={edit.response_at ?? ''} onChange={(v) => set('response_at', v)} /></Field>
                            <Field label="Vrsta odgovora"><select style={inp} value={edit.response_type ?? ''} onChange={(e) => set('response_type', e.target.value)}><option value="">—</option><option value="prigovor_zaprimljen">Prigovor zaprimljen</option><option value="prihvaceno">Prihvaćeno</option><option value="odbijeno">Odbijeno</option></select></Field>
                            <Field label="Datum uplate odštete"><DateInput value={edit.payout_at ?? ''} onChange={(v) => set('payout_at', v)} /></Field>
                            <Field label="Uplaćeni iznos (EUR)"><input style={inp} type="number" value={edit.payout_amount_eur ?? ''} onChange={(e) => set('payout_amount_eur', e.target.value)} /></Field>
                            <Field label="Zaključeno u Farosu (IZGUBLJENO I NAPLAĆENO)"><DateInput value={edit.closed_at ?? ''} onChange={(v) => set('closed_at', v)} /></Field>
                        </div>
                    </div>

                    {edit.id ? (
                        <Field label="Dokument (PDF preskladištenja)">
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input type="file" accept="application/pdf,image/*" onChange={(e) => e.target.files?.[0] && uploadPdf(e.target.files[0])} />
                                {edit.attachment_name ? <a href="#" onClick={(ev) => { ev.preventDefault(); downloadFile(`/parcels/lost/${edit.id}/download`, token, edit.attachment_name!); }}>📎 {edit.attachment_name}</a> : null}
                            </div>
                        </Field>
                    ) : null}

                    <Field label="Napomena"><textarea style={{ ...inp, minHeight: 40 }} value={edit.note ?? ''} onChange={(e) => set('note', e.target.value)} /></Field>

                    {events.length ? (
                        <div style={{ background: '#faf6ec', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#6b5b34', marginBottom: 6 }}>Povijest (tko / kad)</div>
                            {events.map((ev, i) => (
                                <div key={i} style={{ fontSize: '0.76rem', color: '#6b5b34', marginBottom: 2 }}>
                                    <b>{ev.detail}</b> — {ev.by_name} · {ev.at ? formatDateTime(ev.at) : ''}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                        {edit.id ? <Btn variant="danger" onClick={() => { del(edit.id!); setEdit(null); }}>Obriši</Btn> : <span />}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn onClick={() => setEdit(null)}>Odustani</Btn>
                            <Btn variant="primary" disabled={busy} onClick={save}>{busy ? 'Spremam…' : 'Spremi'}</Btn>
                        </div>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────

type DamagedRow = {
    id: number; branch: string | null; article_code: string | null; qty: string | null; damage_comment: string | null;
    returned_at: string | null; new_sent_at: string | null; disposition: string | null; invoice_to_adms: number | null;
    note: string | null; created_by_name: string | null; updated_by_name: string | null; updated_at: string | null;
};

export function DamagedParcelsTab({ token }: { token: string; locale: Locale }) {
    const [rows, setRows] = useState<DamagedRow[]>([]);
    const [summary, setSummary] = useState<Record<string, number>>({});
    const [q, setQ] = useState('');
    const [edit, setEdit] = useState<Partial<DamagedRow> | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        const params = new URLSearchParams(); if (q) params.set('q', q);
        const r = await api<{ rows: DamagedRow[]; summary: Record<string, number> }>(`/parcels/damaged?${params}`, token);
        setRows(r.rows); setSummary(r.summary);
    }, [token, q]);
    useEffect(() => { load().catch((e) => setErr(String(e))); }, [load]);

    const set = (k: keyof DamagedRow, v: unknown) => setEdit((p) => ({ ...(p ?? {}), [k]: v }));

    async function save() {
        if (!edit) return;
        setBusy(true); setErr('');
        try {
            const body: Record<string, unknown> = {
                branch: edit.branch, article_code: edit.article_code, qty: edit.qty === '' ? null : edit.qty,
                damage_comment: edit.damage_comment, returned_at: edit.returned_at, new_sent_at: edit.new_sent_at,
                disposition: edit.disposition, invoice_to_adms: edit.invoice_to_adms == null ? null : !!edit.invoice_to_adms, note: edit.note,
            };
            if (edit.id) await mutate(`/parcels/damaged/${edit.id}`, token, 'PUT', body);
            else await mutate('/parcels/damaged', token, 'POST', body);
            setEdit(null); await load();
        } catch (e) { setErr(String(e)); } finally { setBusy(false); }
    }
    async function del(id: number) { if (!window.confirm('Obrisati zapis?')) return; await mutate(`/parcels/damaged/${id}`, token, 'DELETE'); await load(); }

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <input style={{ ...inp, width: 240 }} placeholder="Pretraga (poslovnica, artikl, komentar…)" value={q} onChange={(e) => setQ(e.target.value)} />
                <div style={{ flex: 1 }} />
                <Btn variant="primary" onClick={() => setEdit({})}>+ Novi zapis</Btn>
                <Btn onClick={() => downloadFile('/parcels/damaged-export', token, 'ostecivani_proizvodi.xlsx')}>⬇ XLS</Btn>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {[['total', 'Ukupno', '#6b5b34'], ['open', 'Bez novog (otvoreno)', '#b7791f'], ['to_invoice_adms', 'Za fakturiranje ADMS', '#2b6cb0']].map(([k, l, c]) => (
                    <div key={k} style={{ ...card, padding: '10px 14px', minWidth: 130 }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c as string }}>{summary[k as string] ?? 0}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{l}</div>
                    </div>
                ))}
            </div>
            {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                    <thead><tr>{['Poslovnica', 'Artikl', 'Kol', 'Oštećenje', 'Vraćen', 'Novi poslan', 'Dispozicija', 'Fakt. ADMS', 'Zadnji', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td style={td}>{r.branch}</td>
                                <td style={td}>{r.article_code}</td>
                                <td style={td}>{r.qty ?? ''}</td>
                                <td style={{ ...td, maxWidth: 220, whiteSpace: 'normal' }}>{r.damage_comment}</td>
                                <td style={td}>{r.returned_at ?? '—'}</td>
                                <td style={td}>{r.new_sent_at ?? '—'}</td>
                                <td style={{ ...td, maxWidth: 180, whiteSpace: 'normal' }}>{r.disposition}</td>
                                <td style={td}>{r.invoice_to_adms == null ? '—' : (r.invoice_to_adms ? <Pill tone="info">DA</Pill> : 'NE')}</td>
                                <td style={td}><span style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{r.updated_by_name ?? r.created_by_name}</span></td>
                                <td style={td}><Btn size="sm" onClick={() => setEdit({ ...r })}>Uredi</Btn></td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td style={td} colSpan={10}>Nema zapisa.</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {edit ? (
                <Modal title={edit.id ? 'Oštećeni proizvod' : 'Novi zapis oštećenja'} onClose={() => setEdit(null)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Field label="Poslovnica"><input style={inp} value={edit.branch ?? ''} onChange={(e) => set('branch', e.target.value)} /></Field>
                        <Field label="Šifra artikla"><input style={inp} value={edit.article_code ?? ''} onChange={(e) => set('article_code', e.target.value)} /></Field>
                        <Field label="Količina"><input style={inp} type="number" value={edit.qty ?? ''} onChange={(e) => set('qty', e.target.value)} /></Field>
                        <Field label="Fakturiranje prema ADMS"><select style={inp} value={edit.invoice_to_adms == null ? '' : String(edit.invoice_to_adms)} onChange={(e) => set('invoice_to_adms', e.target.value === '' ? null : Number(e.target.value))}><option value="">—</option><option value="1">DA</option><option value="0">NE</option></select></Field>
                        <Field label="Datum vraćanja proizvoda"><DateInput value={edit.returned_at ?? ''} onChange={(v) => set('returned_at', v)} /></Field>
                        <Field label="Datum slanja novog proizvoda"><DateInput value={edit.new_sent_at ?? ''} onChange={(v) => set('new_sent_at', v)} /></Field>
                    </div>
                    <Field label="Komentar oštećenja"><textarea style={{ ...inp, minHeight: 50 }} value={edit.damage_comment ?? ''} onChange={(e) => set('damage_comment', e.target.value)} /></Field>
                    <Field label="Što se napravilo s oštećenim"><textarea style={{ ...inp, minHeight: 50 }} value={edit.disposition ?? ''} onChange={(e) => set('disposition', e.target.value)} /></Field>
                    <Field label="Napomena"><textarea style={{ ...inp, minHeight: 40 }} value={edit.note ?? ''} onChange={(e) => set('note', e.target.value)} /></Field>
                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                        {edit.id ? <Btn variant="danger" onClick={() => { del(edit.id!); setEdit(null); }}>Obriši</Btn> : <span />}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn onClick={() => setEdit(null)}>Odustani</Btn>
                            <Btn variant="primary" disabled={busy} onClick={save}>{busy ? 'Spremam…' : 'Spremi'}</Btn>
                        </div>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────

type DriverRow = {
    id: number; ship_date: string | null; packed_date: string | null; status: string | null;
    company_code: string | null; branch: string | null;
    transfer_no: string | null; bag_no: string | null; driver_name: string | null; packages: number | null;
    value_eur: string | null; orders: number | null; route_note: string | null; note: string | null;
    created_by_name: string | null; updated_by_name: string | null;
};

function driverStatusOf(row: Partial<DriverRow>): 'pakirano' | 'poslano' | null {
    if (row.ship_date) return 'poslano';
    if (row.packed_date || row.status === 'pakirano') return 'pakirano';
    if (row.status === 'poslano') return 'poslano';
    return null;
}

export function DriverShipmentsTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<DriverRow[]>([]);
    const [summary, setSummary] = useState<Record<string, number>>({});
    const [edit, setEdit] = useState<Partial<DriverRow> | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [statusF, setStatusF] = useState('');

    const [q, setQ] = useState('');
    const load = useCallback(async () => {
        const params = new URLSearchParams(); if (q) params.set('q', q);
        if (statusF) params.set('status', statusF);
        const r = await api<{ rows: DriverRow[]; summary: Record<string, number> }>(`/parcels/driver?${params}`, token);
        setRows(r.rows); setSummary(r.summary);
    }, [token, q, statusF]);
    useEffect(() => { load().catch((e) => setErr(String(e))); }, [load]);

    const set = (k: keyof DriverRow, v: unknown) => setEdit((p) => ({ ...(p ?? {}), [k]: v }));

    async function save() {
        if (!edit) return;
        setBusy(true); setErr('');
        try {
            const body: Record<string, unknown> = {
                ship_date: edit.ship_date || null,
                packed_date: edit.packed_date || null,
                status: driverStatusOf(edit),
                company_code: edit.company_code, branch: edit.branch,
                transfer_no: edit.transfer_no, bag_no: edit.bag_no, driver_name: edit.driver_name,
                packages: (edit.packages as unknown) === '' || edit.packages == null ? null : edit.packages,
                value_eur: (edit.value_eur as unknown) === '' ? null : edit.value_eur,
                orders: (edit.orders as unknown) === '' || edit.orders == null ? null : edit.orders,
                route_note: edit.route_note, note: edit.note,
            };
            if (edit.id) await mutate(`/parcels/driver/${edit.id}`, token, 'PUT', body);
            else await mutate('/parcels/driver', token, 'POST', body);
            setEdit(null); await load();
        } catch (e) { setErr(String(e)); } finally { setBusy(false); }
    }
    async function del(id: number) { if (!window.confirm(t('Obrisati zapis?', 'Delete record?'))) return; await mutate(`/parcels/driver/${id}`, token, 'DELETE'); await load(); }

    const statusLabel = (s: string | null | undefined) => s === 'pakirano' ? t('Pakirano', 'Packed') : s === 'poslano' ? t('Poslano', 'Sent') : '—';
    const statusTone = (s: string | null | undefined): React.CSSProperties => s === 'pakirano'
        ? { background: 'rgba(183,121,31,0.14)', color: '#8a5a12', fontWeight: 700, padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem' }
        : s === 'poslano'
            ? { background: 'rgba(30,64,175,0.12)', color: '#1e40af', fontWeight: 700, padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem' }
            : {};
    const previewStatus = edit ? driverStatusOf(edit) : null;

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <input style={{ ...inp, width: 240 }} placeholder={t('Pretraga (poslovnica, međuskladišnica, vrećica, vozač…)', 'Search (branch, transfer, bag, driver…)')} value={q} onChange={(e) => setQ(e.target.value)} />
                <select style={{ ...inp, width: 160 }} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
                    <option value="">{t('Svi statusi', 'All statuses')}</option>
                    <option value="pakirano">{t('Pakirano', 'Packed')}</option>
                    <option value="poslano">{t('Poslano', 'Sent')}</option>
                </select>
                <div style={{ flex: 1 }} />
                <Btn variant="primary" onClick={() => setEdit({ company_code: 'ADMS' })}>+ {t('Nova dostava', 'New delivery')}</Btn>
                <Btn onClick={() => downloadFile('/parcels/driver-export', token, 'dostave_vozacem.xlsx')}>⬇ XLS</Btn>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {([['entries', t('Unosa', 'Entries'), '#6b5b34'], ['pakirano', t('Pakirano', 'Packed'), '#b7791f'], ['poslano', t('Poslano', 'Sent'), '#1e40af'], ['packages', t('Paketa', 'Packages'), '#2b6cb0'], ['orders', t('Narudžbi', 'Orders'), '#b7791f']] as const).map(([k, l, c]) => (
                    <div key={k} style={{ ...card, padding: '10px 14px', minWidth: 110 }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c }}>{summary[k] ?? 0}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{l}</div>
                    </div>
                ))}
                <div style={{ ...card, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#6b5b34' }}>{formatMoney(summary.value ?? 0, locale)}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{t('Vrijednost', 'Value')}</div>
                </div>
            </div>
            {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 880 }}>
                    <thead><tr>{[t('Datum slanja', 'Ship date'), t('Datum pakiranja', 'Packing date'), t('Status', 'Status'), t('Firma', 'Company'), t('Poslovnica', 'Branch'), t('Međuskladišnica', 'Transfer'), t('Vrećica', 'Bag'), t('Vozač', 'Driver'), t('Vrijednost', 'Value'), t('Unio', 'Entered by'), ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={r.status === 'pakirano' ? { background: 'rgba(183,121,31,0.06)' } : undefined}>
                                <td style={td}>{r.ship_date ? formatDate(r.ship_date, locale) : '—'}</td>
                                <td style={td}>{r.packed_date ? formatDate(r.packed_date, locale) : '—'}</td>
                                <td style={td}><span style={statusTone(r.status)}>{statusLabel(r.status)}</span></td>
                                <td style={td}>{r.company_code}</td>
                                <td style={td}>{r.branch}</td>
                                <td style={td}>{r.transfer_no}</td>
                                <td style={td}>{r.bag_no}</td>
                                <td style={td}>{r.driver_name}</td>
                                <td style={td}>{r.value_eur ? formatMoney(Number(r.value_eur), locale) : '—'}</td>
                                <td style={td}><span style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{r.updated_by_name ?? r.created_by_name}</span></td>
                                <td style={td}><Btn size="sm" onClick={() => setEdit({ ...r })}>{t('Uredi', 'Edit')}</Btn></td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td style={td} colSpan={11}>{t('Nema zapisa.', 'No records.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {edit ? (
                <Modal title={edit.id ? t('Dostava vozačem', 'Driver delivery') : t('Nova dostava vozačem', 'New driver delivery')} onClose={() => setEdit(null)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Field label={t('Datum pakiranja', 'Packing date')}>
                            <DateInput value={edit.packed_date ?? ''} onChange={(v) => set('packed_date', v)} />
                        </Field>
                        <Field label={t('Ime vozača', 'Driver name')}>
                            <input style={inp} value={edit.driver_name ?? ''} onChange={(e) => set('driver_name', e.target.value)} placeholder={t('npr. Mario', 'e.g. Mario')} />
                        </Field>
                        <Field label={t('Datum slanja', 'Ship date')}>
                            <DateInput value={edit.ship_date ?? ''} onChange={(v) => set('ship_date', v)} />
                        </Field>
                        <Field label={t('Status', 'Status')}>
                            <div style={{ ...inp, display: 'flex', alignItems: 'center', gap: 8, background: '#faf7ef' }}>
                                <span style={statusTone(previewStatus)}>{statusLabel(previewStatus)}</span>
                                <span style={{ fontSize: '0.72rem', color: '#8a7a52' }}>
                                    {previewStatus === 'pakirano'
                                        ? t('Spakirano, nije poslano.', 'Packed, not sent.')
                                        : previewStatus === 'poslano'
                                            ? t('Poslano vozačem.', 'Sent with driver.')
                                            : t('Unesi datum pakiranja ili slanja.', 'Enter packing or ship date.')}
                                </span>
                            </div>
                        </Field>
                        <Field label={t('Firma', 'Company')}><select style={inp} value={edit.company_code ?? ''} onChange={(e) => set('company_code', e.target.value)}><option value="ADMS">ADMS</option><option value="ADO">ADO</option></select></Field>
                        <Field label={t('Poslovnica', 'Branch')}><input style={inp} value={edit.branch ?? ''} onChange={(e) => set('branch', e.target.value)} placeholder={t('npr. ZG MAKSIMIR', 'e.g. ZG MAKSIMIR')} /></Field>
                        <Field label={t('Broj međuskladišnice', 'Transfer no.')}><input style={inp} value={edit.transfer_no ?? ''} onChange={(e) => set('transfer_no', e.target.value)} placeholder={t('npr. 4744/2026', 'e.g. 4744/2026')} /></Field>
                        <Field label={t('Broj vrećice', 'Bag no.')}><input style={inp} value={edit.bag_no ?? ''} onChange={(e) => set('bag_no', e.target.value)} placeholder={t('npr. 0756715', 'e.g. 0756715')} /></Field>
                        <Field label={t('Vrijednost (EUR)', 'Value (EUR)')}><input style={inp} type="number" value={edit.value_eur ?? ''} onChange={(e) => set('value_eur', e.target.value)} /></Field>
                        <Field label={t('Broj narudžbi (opcionalno)', 'Order count (optional)')}><input style={inp} type="number" value={edit.orders ?? ''} onChange={(e) => set('orders', e.target.value)} /></Field>
                    </div>
                    <Field label={t('Napomena', 'Note')}><textarea style={{ ...inp, minHeight: 40 }} value={edit.note ?? ''} onChange={(e) => set('note', e.target.value)} /></Field>
                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                        {edit.id ? <Btn variant="danger" onClick={() => { del(edit.id!); setEdit(null); }}>{t('Obriši', 'Delete')}</Btn> : <span />}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn onClick={() => setEdit(null)}>{t('Odustani', 'Cancel')}</Btn>
                            <Btn variant="primary" disabled={busy} onClick={save}>{busy ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}</Btn>
                        </div>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────

type CodOrder = {
    id: number; order_no: string; fiscal_no: string | null; doc_date: string | null; due_date: string | null;
    customer_code: string | null; customer_name: string | null; amount_eur: string | null; paid_at: string | null;
    note: string | null; updated_by_name: string | null;
};
type CodPayment = { id: number; doc_date: string | null; value_date: string | null; payer: string | null; amount_eur: string | null };

/**
 * Pouzeća (web) — kontrola plaćanja web narudžbi pouzećem (Ružica). HP zbirno uplaćuje na ADO;
 * ovdje se po narudžbi unosi datum uplate (inline). "Otvoreno" — bilo tko s pristupom (zamjena
 * dok je netko na godišnjem); tko/kad se bilježi.
 */
export function CodControlTab({ token }: { token: string; locale: Locale }) {
    const [rows, setRows] = useState<CodOrder[]>([]);
    const [summary, setSummary] = useState<Record<string, number>>({});
    const [payments, setPayments] = useState<CodPayment[] | null>(null);
    const [showPayments, setShowPayments] = useState(false);
    const [statusF, setStatusF] = useState('unpaid');
    const [q, setQ] = useState('');
    const [err, setErr] = useState('');
    const [impBusy, setImpBusy] = useState(false);
    const [impMsg, setImpMsg] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);

    const load = useCallback(async () => {
        const params = new URLSearchParams();
        if (statusF) params.set('status', statusF);
        if (q) params.set('q', q);
        const r = await api<{ rows: CodOrder[]; summary: Record<string, number> }>(`/parcels/cod?${params}`, token);
        setRows(r.rows); setSummary(r.summary);
    }, [token, statusF, q]);
    useEffect(() => { load().catch((e) => setErr(String(e))); }, [load]);

    useEffect(() => {
        if (showPayments && payments === null) {
            api<{ rows: CodPayment[] }>('/parcels/cod-payments', token).then((r) => setPayments(r.rows)).catch(() => setPayments([]));
        }
    }, [showPayments, payments, token]);

    async function doImport(file: File) {
        setImpBusy(true); setImpMsg(''); setErr('');
        try {
            const fd = new FormData(); fd.append('file', file);
            const res = await fetch(`${API_BASE}/parcels/cod-import`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
            const j = await res.json();
            if (!res.ok) throw new Error(j?.message || j?.error || `HTTP ${res.status}`);
            setImpMsg(`✓ Narudžbe: +${j.orders_inserted} novih, ${j.orders_updated} ažurirano (${j.paid_filled} datuma uplate iz filea) · Uplate: +${j.payments_inserted} (${j.payments_skipped} već postoji)`);
            setPayments(null); await load();
        } catch (e) { setErr(e instanceof Error ? e.message : 'Uvoz nije uspio'); } finally { setImpBusy(false); }
    }

    async function setPaid(row: CodOrder, iso: string) {
        setSavingId(row.id);
        try {
            await mutate(`/parcels/cod/${row.id}`, token, 'PUT', { paid_at: iso || null });
            setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, paid_at: iso || null } : r)));
            // summary se osvježi tiho (bez resetiranja filtera/scrolla)
            api<{ summary: Record<string, number> }>(`/parcels/cod?status=`, token).then((r) => setSummary(r.summary)).catch(() => undefined);
        } catch (e) { setErr(String(e)); } finally { setSavingId(null); }
    }

    const eur = (v: number | undefined) => formatMoney(v ?? 0);

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <select style={{ ...inp, width: 170 }} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
                    <option value="unpaid">Neplaćene</option>
                    <option value="paid">Plaćene</option>
                    <option value="">Sve</option>
                </select>
                <input style={{ ...inp, width: 230 }} placeholder="Pretraga (broj, fiskalni, kupac…)" value={q} onChange={(e) => setQ(e.target.value)} />
                <div style={{ flex: 1 }} />
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b5b34', cursor: 'pointer', border: '1px solid #d8cdb2', borderRadius: 8, padding: '6px 10px', background: '#fff' }}>
                    ⬆ {impBusy ? 'Uvozim…' : 'Uvoz xlsx (kontrola)'}
                    <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} disabled={impBusy}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) void doImport(f); e.target.value = ''; }} />
                </label>
                <Btn onClick={() => downloadFile('/parcels/cod-export', token, 'pouzeca_kontrola.xlsx')}>⬇ XLS</Btn>
            </div>

            {impMsg ? <div style={{ fontSize: '0.8rem', color: '#1f7a36', marginBottom: 8 }}>{impMsg}</div> : null}
            {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ ...card, padding: '10px 14px', minWidth: 120 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#6b5b34' }}>{summary.total ?? 0}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>Narudžbi ukupno</div>
                </div>
                <div style={{ ...card, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#c0392b' }}>{summary.unpaid ?? 0}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>Neplaćeno · {eur(summary.unpaid_eur)}</div>
                </div>
                <div style={{ ...card, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2f855a' }}>{summary.paid ?? 0}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>Plaćeno · {eur(summary.paid_eur)}</div>
                </div>
                <div style={{ ...card, padding: '10px 14px', minWidth: 170, cursor: 'pointer' }} onClick={() => setShowPayments((s) => !s)} title="Klik za popis HP uplata">
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2b6cb0' }}>{eur(summary.payments_eur)}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>HP uplate ({summary.payments_count ?? 0}) {showPayments ? '▲' : '▼'}</div>
                </div>
            </div>

            {showPayments ? (
                <div style={{ ...card, padding: 0, overflowX: 'auto', marginBottom: 12, maxHeight: 260, overflowY: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
                        <thead><tr>{['Datum dok.', 'Datum valute', 'Platitelj', 'Uplata'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                        <tbody>
                            {(payments ?? []).map((p) => (
                                <tr key={p.id}>
                                    <td style={td}>{p.doc_date ?? '—'}</td>
                                    <td style={td}>{p.value_date ?? '—'}</td>
                                    <td style={td}>{p.payer}</td>
                                    <td style={td}>{p.amount_eur ? formatMoney(Number(p.amount_eur)) : '—'}</td>
                                </tr>
                            ))}
                            {payments !== null && payments.length === 0 ? <tr><td style={td} colSpan={4}>Nema uplata.</td></tr> : null}
                            {payments === null ? <tr><td style={td} colSpan={4}>Učitavam…</td></tr> : null}
                        </tbody>
                    </table>
                </div>
            ) : null}

            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 940 }}>
                    <thead><tr>{['Broj', 'Fiskalni broj', 'Datum dok.', 'Dospijeće', 'Kupac', 'Iznos', 'Datum uplate', 'Unio'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={r.paid_at ? undefined : { background: '#fff9f2' }}>
                                <td style={td}>{r.order_no}</td>
                                <td style={td}>{r.fiscal_no}</td>
                                <td style={td}>{r.doc_date ?? '—'}</td>
                                <td style={td}>{r.due_date ?? '—'}</td>
                                <td style={td}><MaskedName name={r.customer_name} />{r.customer_code ? <span style={{ fontSize: '0.7rem', color: '#8a7a52' }}> ({r.customer_code})</span> : null}</td>
                                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.amount_eur ? formatMoney(Number(r.amount_eur)) : '—'}</td>
                                <td style={{ ...td, minWidth: 150 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <DateInput value={r.paid_at ?? ''} onChange={(v) => { if (v !== (r.paid_at ?? '')) void setPaid(r, v); }} />
                                        {savingId === r.id ? <span style={{ fontSize: '0.7rem', color: '#8a7a52' }}>…</span> : (r.paid_at ? <span style={{ color: '#2f855a', fontWeight: 700 }}>✓</span> : null)}
                                    </div>
                                </td>
                                <td style={td}><span style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{r.updated_by_name ?? ''}</span></td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td style={td} colSpan={8}>Nema narudžbi za odabrani filter.</td></tr> : null}
                    </tbody>
                </table>
                <div style={{ fontSize: '0.72rem', color: '#8a7a52', padding: '6px 10px' }}>{rows.length} narudžbi · datum uplate se sprema odmah pri unosu</div>
            </div>
        </div>
    );
}
